import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  updateDoc,
  query,
  where,
  orderBy,
  Timestamp,
} from 'firebase/firestore';
import { db } from '@/lib/firebase';
import type { LoanType } from '@/types';
import { normalizeLoanType } from '@/types';
import type {
  ChecklistTemplate,
  ChecklistForm,
  ChecklistResponse,
  MergedChecklistForm,
  ChecklistCategory,
  ChecklistStats,
  CategoryStats,
} from '@/types/checklist';

// ============================================================================
// COLLECTION REFERENCES
// ============================================================================

const templatesCollection = collection(db, 'checklist_templates');
const formsCollection = collection(db, 'checklist_forms');
const responsesCollection = collection(db, 'checklist_responses');

// ============================================================================
// TIMESTAMP HELPERS
// ============================================================================

/**
 * Convert Firestore timestamp to Date
 */
const convertTimestamp = (timestamp: Date | Timestamp | undefined): Date => {
  if (timestamp instanceof Date) return timestamp;
  if (timestamp && typeof timestamp === 'object' && 'toDate' in timestamp) {
    return timestamp.toDate();
  }
  return new Date();
};

/**
 * Convert Date to Firestore Timestamp
 */
const toTimestamp = (date: Date | Timestamp | undefined): Timestamp => {
  if (date instanceof Timestamp) return date;
  if (date instanceof Date && !isNaN(date.getTime())) {
    return Timestamp.fromDate(date);
  }
  return Timestamp.now();
};

/**
 * Remove undefined fields from an object (Firestore doesn't accept undefined)
 */
const removeUndefinedFields = <T extends Record<string, any>>(obj: T): Partial<T> => {
  const cleaned: Partial<T> = {};
  Object.keys(obj).forEach((key) => {
    const value = obj[key];
    if (value !== undefined) {
      cleaned[key as keyof T] = value;
    }
  });
  return cleaned;
};

// ============================================================================
// TEMPLATE OPERATIONS
// ============================================================================

/**
 * Get base template for a loan type
 */
export const getChecklistTemplate = async (loanType: LoanType | undefined): Promise<ChecklistTemplate | null> => {
  try {
    if (!loanType) {
      console.log('No loan type provided to getChecklistTemplate');
      return null;
    }
    // Older leads can have loanType stored as a label ("Home Loan") instead
    // of the canonical key ("home_loan") — normalize before the doc lookup.
    const docRef = doc(templatesCollection, normalizeLoanType(loanType) || loanType);
    const docSnap = await getDoc(docRef);
    
    if (!docSnap.exists()) {
      console.log(`No checklist template found for ${loanType}`);
      return null;
    }
    
    const data = docSnap.data();
    return {
      ...data,
      id: docSnap.id,
      createdAt: convertTimestamp(data.createdAt),
      updatedAt: convertTimestamp(data.updatedAt),
    } as ChecklistTemplate;
  } catch (error) {
    console.error(`Error fetching checklist template for ${loanType}:`, error);
    return null;
  }
};

/**
 * Get all active templates
 */
export const getAllChecklistTemplates = async (): Promise<ChecklistTemplate[]> => {
  try {
    const q = query(templatesCollection, where('isActive', '==', true));
    const querySnapshot = await getDocs(q);
    
    return querySnapshot.docs.map(docSnap => {
      const data = docSnap.data();
      return {
        ...data,
        id: docSnap.id,
        createdAt: convertTimestamp(data.createdAt),
        updatedAt: convertTimestamp(data.updatedAt),
      } as ChecklistTemplate;
    });
  } catch (error) {
    console.error('Error fetching all checklist templates:', error);
    return [];
  }
};

/**
 * Create a new checklist template
 */
export const createChecklistTemplate = async (
  template: Omit<ChecklistTemplate, 'id' | 'createdAt' | 'updatedAt'>
): Promise<string> => {
  try {
    const docRef = doc(templatesCollection, template.loanType);
    const now = Timestamp.now();
    
    const templateData = {
      ...template,
      createdAt: now,
      updatedAt: now,
    };
    
    await setDoc(docRef, templateData);
    return docRef.id;
  } catch (error) {
    console.error('Error creating checklist template:', error);
    throw error;
  }
};

/**
 * Update an existing checklist template
 */
export const updateChecklistTemplate = async (
  loanType: LoanType,
  updates: Partial<Omit<ChecklistTemplate, 'id' | 'loanType' | 'createdAt'>>
): Promise<void> => {
  try {
    const docRef = doc(templatesCollection, loanType);
    const now = Timestamp.now();
    
    const updateData = removeUndefinedFields({
      ...updates,
      updatedAt: now,
    });
    
    await updateDoc(docRef, updateData);
  } catch (error) {
    console.error(`Error updating checklist template ${loanType}:`, error);
    throw error;
  }
};

// ============================================================================
// LENDER FORM OPERATIONS
// ============================================================================

/**
 * Get lender-specific checklist form
 */
export const getLenderChecklistForm = async (
  loanType: LoanType,
  lenderId: string
): Promise<ChecklistForm | null> => {
  try {
    const formId = `${normalizeLoanType(loanType) || loanType}_${lenderId}`;
    const docRef = doc(formsCollection, formId);
    const docSnap = await getDoc(docRef);
    
    if (!docSnap.exists()) {
      return null;
    }
    
    const data = docSnap.data();
    
    // Only return published forms
    if (data.status !== 'published') {
      return null;
    }
    
    return {
      ...data,
      id: docSnap.id,
      createdAt: convertTimestamp(data.createdAt),
      updatedAt: convertTimestamp(data.updatedAt),
      publishedAt: convertTimestamp(data.publishedAt),
    } as ChecklistForm;
  } catch (error) {
    console.error(`Error fetching lender checklist form ${loanType}_${lenderId}:`, error);
    return null;
  }
};

/**
 * Get all lender-specific forms for a loan type
 */
export const getAllLenderChecklistForms = async (loanType?: LoanType): Promise<ChecklistForm[]> => {
  try {
    let q;
    if (loanType) {
      q = query(formsCollection, where('loanType', '==', loanType));
    } else {
      q = query(formsCollection);
    }
    
    const querySnapshot = await getDocs(q);
    
    return querySnapshot.docs.map(docSnap => {
      const data = docSnap.data() as any;
      return {
        ...data,
        id: docSnap.id,
        createdAt: convertTimestamp(data.createdAt),
        updatedAt: convertTimestamp(data.updatedAt),
        publishedAt: convertTimestamp(data.publishedAt),
      } as ChecklistForm;
    });
  } catch (error) {
    console.error('Error fetching lender checklist forms:', error);
    return [];
  }
};

/**
 * Create a new lender-specific checklist form
 */
export const createLenderChecklistForm = async (
  form: Omit<ChecklistForm, 'id' | 'createdAt' | 'updatedAt'>
): Promise<string> => {
  try {
    const formId = form.lenderId ? `${form.loanType}_${form.lenderId}` : form.loanType;
    const docRef = doc(formsCollection, formId);
    const now = Timestamp.now();
    
    const formData: any = {
      ...form,
      createdAt: now,
      updatedAt: now,
    };
    
    // Only add publishedAt if status is published
    if (form.status === 'published') {
      formData.publishedAt = now;
    }
    
    await setDoc(docRef, removeUndefinedFields(formData));
    return docRef.id;
  } catch (error) {
    console.error('Error creating lender checklist form:', error);
    throw error;
  }
};

/**
 * Update an existing lender-specific checklist form
 */
export const updateLenderChecklistForm = async (
  formId: string,
  updates: Partial<Omit<ChecklistForm, 'id' | 'createdAt'>>
): Promise<void> => {
  try {
    const docRef = doc(formsCollection, formId);
    const now = Timestamp.now();
    
    // If status is being changed to published, set publishedAt
    const updateData: any = {
      ...updates,
      updatedAt: now,
    };
    
    if (updates.status === 'published') {
      updateData.publishedAt = now;
    }
    
    await updateDoc(docRef, removeUndefinedFields(updateData));
  } catch (error) {
    console.error(`Error updating lender checklist form ${formId}:`, error);
    throw error;
  }
};

// ============================================================================
// MERGED FORM FETCHING (CLIENT-SIDE)
// ============================================================================

/**
 * Get checklist form for a lead (with fallback logic)
 * This is the main function used by clients
 * 
 * Resolution order:
 * 1. If lenderId provided → Try lender-specific form (if published)
 * 2. Fall back to base template
 */
export const getChecklistForm = async (
  loanType: LoanType | undefined,
  lenderId?: string
): Promise<MergedChecklistForm | null> => {
  try {
    if (!loanType) {
      console.log('No loan type provided to getChecklistForm');
      return null;
    }
    // Try lender-specific form first
    if (lenderId) {
      const lenderForm = await getLenderChecklistForm(loanType, lenderId);
      if (lenderForm && lenderForm.status === 'published') {
        return {
          ...lenderForm,
          isFromTemplate: false,
          isVersionLocked: false,
        };
      }
    }
    
    // Fall back to base template
    const template = await getChecklistTemplate(loanType);
    if (template && template.isActive) {
      // Convert template to form format
      return {
        ...template,
        baseTemplateId: template.id,
        lenderId: undefined,
        lenderName: undefined,
        status: 'published',
        isFromTemplate: true,
        isVersionLocked: false,
        publishedAt: template.createdAt,
      } as MergedChecklistForm;
    }
    
    return null;
  } catch (error) {
    console.error('Error getting checklist form:', error);
    return null;
  }
};

/**
 * Get checklist form for a specific lead (with version locking)
 * 
 * If the lead already has a checklist response, returns the form version
 * that was locked when they first checked an item.
 */
export const getChecklistFormForLead = async (
  loanType: LoanType | undefined,
  lenderId: string | undefined,
  leadId: string
): Promise<MergedChecklistForm | null> => {
  try {
    if (!loanType) {
      console.log('No loan type provided for lead checklist');
      return null;
    }
    // Check if lead has existing response with version lock
    const existingResponse = await getChecklistResponse(leadId);
    
    if (existingResponse && existingResponse.formVersionLockedAt) {
      // Lead has version-locked checklist - fetch that specific version
      const formId = existingResponse.formId;
      const docRef = doc(formsCollection, formId);
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists()) {
        const data = docSnap.data() as any;
        return {
          ...data,
          id: docSnap.id,
          createdAt: convertTimestamp(data.createdAt),
          updatedAt: convertTimestamp(data.updatedAt),
          publishedAt: convertTimestamp(data.publishedAt),
          isFromTemplate: !data.lenderId,
          isVersionLocked: true,
          existingResponse,
        } as MergedChecklistForm;
      }
    }
    
    // No version lock - get latest form
    const form = await getChecklistForm(loanType, lenderId);
    if (form && existingResponse) {
      form.existingResponse = existingResponse;
    }
    return form;
  } catch (error) {
    console.error('Error getting checklist form for lead:', error);
    return null;
  }
};

// ============================================================================
// RESPONSE OPERATIONS
// ============================================================================

/**
 * Get checklist response for a lead
 */
export const getChecklistResponse = async (leadId: string): Promise<ChecklistResponse | null> => {
  try {
    const docRef = doc(responsesCollection, leadId);
    const docSnap = await getDoc(docRef);
    
    if (!docSnap.exists()) {
      return null;
    }
    
    const data = docSnap.data();
    return {
      ...data,
      id: docSnap.id,
      createdAt: convertTimestamp(data.createdAt),
      updatedAt: convertTimestamp(data.updatedAt),
      lastCheckedAt: convertTimestamp(data.lastCheckedAt),
      formVersionLockedAt: convertTimestamp(data.formVersionLockedAt),
    } as ChecklistResponse;
  } catch (error) {
    console.error(`Error fetching checklist response for lead ${leadId}:`, error);
    return null;
  }
};

/**
 * Save checklist response (with version locking on first check)
 */
export const saveChecklistResponse = async (
  response: Omit<ChecklistResponse, 'createdAt' | 'updatedAt'>
): Promise<void> => {
  try {
    const docRef = doc(responsesCollection, response.leadId);
    const now = Timestamp.now();
    
    // Check if response already exists
    const existing = await getChecklistResponse(response.leadId);
    
    if (existing) {
      // Update existing - preserve version lock
      const updateData = removeUndefinedFields({
        ...response,
        formVersion: existing.formVersion,  // Keep locked version
        templateVersion: existing.templateVersion,
        formVersionLockedAt: existing.formVersionLockedAt,
        updatedAt: now,
        lastCheckedAt: now,
      });
      await updateDoc(docRef, updateData);
    } else {
      // Create new - lock current version
      const createData = removeUndefinedFields({
        ...response,
        formVersion: response.formVersion || 1,
        templateVersion: response.templateVersion || 1,
        formVersionLockedAt: now,  // Lock version on first check
        createdAt: now,
        updatedAt: now,
        lastCheckedAt: now,
      });
      await setDoc(docRef, createData);
    }
  } catch (error) {
    console.error('Error saving checklist response:', error);
    throw error;
  }
};

/**
 * Toggle a checklist item (check/uncheck)
 */
export const toggleChecklistItem = async (
  leadId: string,
  itemId: string,
  formId: string,
  formVersion: number,
  templateVersion: number,
  totalItems: number
): Promise<void> => {
  try {
    const existingResponse = await getChecklistResponse(leadId);
    
    const checkedItems = existingResponse?.checkedItems || [];
    const isCurrentlyChecked = checkedItems.includes(itemId);
    
    const updatedCheckedItems = isCurrentlyChecked
      ? checkedItems.filter(id => id !== itemId)
      : [...checkedItems, itemId];
    
    const checkedCount = updatedCheckedItems.length;
    const progress = totalItems > 0 ? Math.round((checkedCount / totalItems) * 100) : 0;
    
    // Build response object, only include notes if it exists
    const responseData: any = {
      id: leadId,
      leadId,
      formId,
      formVersion,
      templateVersion,
      checkedItems: updatedCheckedItems,
      totalItems,
      checkedCount,
      progress,
    };
    
    // Only add notes if it's defined and not empty
    if (existingResponse?.notes && Object.keys(existingResponse.notes).length > 0) {
      responseData.notes = existingResponse.notes;
    }
    
    await saveChecklistResponse(responseData);
  } catch (error) {
    console.error('Error toggling checklist item:', error);
    throw error;
  }
};

/**
 * Add or update a note for a checklist item
 */
export const updateChecklistItemNote = async (
  leadId: string,
  itemId: string,
  note: string
): Promise<void> => {
  try {
    const existingResponse = await getChecklistResponse(leadId);
    
    if (!existingResponse) {
      throw new Error('Checklist response not found. Please check at least one item first.');
    }
    
    const updatedNotes = {
      ...existingResponse.notes,
      [itemId]: note,
    };
    
    await saveChecklistResponse({
      ...existingResponse,
      notes: updatedNotes,
    });
  } catch (error) {
    console.error('Error updating checklist item note:', error);
    throw error;
  }
};

// ============================================================================
// STATISTICS & UTILITIES
// ============================================================================

/**
 * Calculate checklist statistics
 */
export const calculateChecklistStats = (
  checklist: ChecklistForm | ChecklistTemplate,
  response?: ChecklistResponse | null
): ChecklistStats => {
  const allItems = checklist.categories.flatMap(cat => cat.items);
  const totalItems = allItems.length;
  const requiredItems = allItems.filter(item => item.isRequired).length;
  
  const checkedItems = response?.checkedCount || 0;
  const checkedItemIds = response?.checkedItems || [];
  const requiredChecked = allItems.filter(
    item => item.isRequired && checkedItemIds.includes(item.id)
  ).length;
  
  const progress = totalItems > 0 ? Math.round((checkedItems / totalItems) * 100) : 0;
  const isComplete = requiredItems > 0 ? requiredChecked === requiredItems : checkedItems === totalItems;
  
  return {
    totalItems,
    checkedItems,
    requiredItems,
    requiredChecked,
    progress,
    isComplete,
  };
};

/**
 * Calculate statistics per category
 */
export const calculateCategoryStats = (
  category: ChecklistCategory,
  response?: ChecklistResponse | null
): CategoryStats => {
  const totalItems = category.items.length;
  const checkedItemIds = response?.checkedItems || [];
  const checkedItems = category.items.filter(item => checkedItemIds.includes(item.id)).length;
  const progress = totalItems > 0 ? Math.round((checkedItems / totalItems) * 100) : 0;
  const isComplete = checkedItems === totalItems;
  
  return {
    categoryId: category.id,
    categoryName: category.name,
    totalItems,
    checkedItems,
    progress,
    isComplete,
  };
};
